import { type PoGetConfigResponse, poUpdateConfig } from '@repo/sdk/gen'
import { formatError } from '@repo/ui/utils'
import {
	useMutation,
	useQueryClient,
	useSuspenseQuery
} from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { pageTitle, type TKey, translateApp, useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/lib/query-keys'
import {
	LotSteps,
	NumField,
	PercentField,
	RetentionCurve,
	SettingList,
	SwitchField
} from '@/routes/(private)/configuration/-components/config-fields'
import {
	ConfigSection,
	HistoryRail,
	SaveBar,
	SectionNav
} from '@/routes/(private)/configuration/-components/config-layout'
import { poConfigQueryOptions } from '@/routes/(private)/documents/-lib/query-options'

export const Route = createFileRoute('/(private)/configuration/')({
	staticData: { breadcrumb: 'po:config.page_title' },
	loader: ({ context }) =>
		context.queryClient.ensureQueryData(poConfigQueryOptions(true)),
	component: Configuration,
	head: () => ({
		meta: [{ title: pageTitle(translateApp('it', 'po:config.page_title')) }]
	})
})

// TODO(handover): this screen is incomplete. `agentSyncHours` comes back from
// get-config and is saved back untouched, but has no control here. The screen
// is also missing from the sidebar (see admin-nav-groups.tsx): today it is
// only reachable from the user menu. See HANDOVER.md §6 in the knowledge base.
function Configuration() {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const query = useSuspenseQuery(poConfigQueryOptions(true))
	const saved = query.data.config
	const [draft, setDraft] = useState<BusinessConfig>(saved)
	// «Annulla» bumps this: the numeric fields are uncontrolled, so going back
	// to the saved values means remounting them.
	const [formVersion, setFormVersion] = useState(0)
	const dirty = JSON.stringify(draft) !== JSON.stringify(saved)

	const updateConfig = useMutation({
		mutationFn: (config: BusinessConfig) =>
			poUpdateConfig({ body: { data: { config } } }),
		onSuccess: async (response) => {
			await queryClient.invalidateQueries({
				queryKey: makeQueryKeys.po.config()
			})
			// The saved config comes back in the database's key order: taking it
			// as the draft keeps «unsaved changes» honest right after a save.
			setDraft(response.data.config)
			// No toast: it would land on the save button and hide its check, and
			// «vale dalle prossime proposte» is already the page's subtitle.
		},
		onError: (error) => toast.error(formatError(error))
	})

	const setSafety = (
		key: 'safetyDefault' | 'safetyEsclusiva',
		fraction: number
	) => {
		setDraft((prev) => ({ ...prev, [key]: fraction }))
	}

	const setAdoptionWeighting = (enabled: boolean) => {
		setDraft((prev) => ({
			...prev,
			tuning: { ...prev.tuning, adoptionRegionWeighting: enabled }
		}))
	}

	const setTuningNum = (key: TuningNumKey, value: number) => {
		setDraft((prev) => ({
			...prev,
			tuning: { ...prev.tuning, [key]: value }
		}))
	}

	const setRetentionAge = (index: number, value: number) => {
		setDraft((prev) => {
			// structuredClone preserves the fixed-length tuple type, so the
			// index write type-checks without widening to number[].
			const retentionDefaults = structuredClone(
				prev.tuning.retentionDefaults
			)
			retentionDefaults[index] = value
			return { ...prev, tuning: { ...prev.tuning, retentionDefaults } }
		})
	}

	const patchLotStep = (index: number, patch: Partial<LotStep>) => {
		setDraft((prev) => ({
			...prev,
			tuning: {
				...prev.tuning,
				lotSteps: prev.tuning.lotSteps.map((step, i) =>
					i === index ? { ...step, ...patch } : step
				)
			}
		}))
	}

	const addLotStep = () => {
		setDraft((prev) => ({
			...prev,
			tuning: {
				...prev.tuning,
				lotSteps: [...prev.tuning.lotSteps, { upTo: null, step: 10 }]
			}
		}))
	}

	const removeLotStep = (index: number) => {
		setDraft((prev) => ({
			...prev,
			tuning: {
				...prev.tuning,
				lotSteps: prev.tuning.lotSteps.filter((_, i) => i !== index)
			}
		}))
	}

	return (
		<div className='flex flex-1 flex-col'>
			<div className='mx-auto grid w-full max-w-7xl gap-x-10 gap-y-6 px-4 pt-6 pb-10 md:px-6 lg:grid-cols-[12rem_minmax(0,1fr)] 2xl:max-w-[96rem] 2xl:grid-cols-[12rem_minmax(0,1fr)_18rem]'>
				<header className='animate-enter lg:col-span-2 2xl:col-span-3'>
					<h1 className='font-semibold text-2xl tracking-tight'>
						{t('po:config.page_title')}
					</h1>
					<p className='mt-1 text-muted-foreground text-sm'>
						{t('po:config.effect_notice')}
					</p>
				</header>

				<SectionNav sections={SECTIONS} />

				<div className='flex min-w-0 flex-col gap-5' key={formVersion}>
					<ConfigSection
						description={t('po:config.safety_description')}
						enter={1}
						example={t('po:config.safety_example')}
						id='safety'
						title={t('po:config.safety_title')}
					>
						<SettingList>
							<PercentField
								label={t('po:config.safety_default')}
								onCommit={(fraction) =>
									setSafety('safetyDefault', fraction)
								}
								value={draft.safetyDefault}
							/>
							<PercentField
								label={t('po:config.safety_esclusiva')}
								onCommit={(fraction) =>
									setSafety('safetyEsclusiva', fraction)
								}
								value={draft.safetyEsclusiva}
							/>
						</SettingList>
					</ConfigSection>

					{TUNING_NUM_GROUPS.map((group, index) => (
						<ConfigSection
							description={t(
								`po:config.tuning.${group.name}_description`
							)}
							enter={index === 0 ? 2 : 3}
							example={t(
								`po:config.tuning.${group.name}_example`
							)}
							id={group.name}
							key={group.name}
							title={t(`po:config.tuning.${group.name}_title`)}
						>
							<SettingList>
								{group.keys.map((key) => (
									<NumField
										key={key}
										label={t(`po:config.tuning.${key}`)}
										onCommit={(value) =>
											setTuningNum(key, value)
										}
										value={draft.tuning[key]}
									/>
								))}
							</SettingList>
						</ConfigSection>
					))}

					<ConfigSection
						description={t('po:config.tuning.adoption_description')}
						enter={3}
						example={t('po:config.tuning.adoption_example')}
						id='adoption'
						title={t('po:config.tuning.adoption_title')}
					>
						<SettingList>
							<SwitchField
								checked={draft.tuning.adoptionRegionWeighting}
								label={t(
									'po:config.tuning.adoptionRegionWeighting'
								)}
								onCheckedChange={setAdoptionWeighting}
							/>
							{ADOPTION_NUM_KEYS.map((key) => (
								<NumField
									key={key}
									label={t(`po:config.tuning.${key}`)}
									onCommit={(value) =>
										setTuningNum(key, value)
									}
									value={draft.tuning[key]}
								/>
							))}
						</SettingList>
					</ConfigSection>

					<ConfigSection
						description={t(
							'po:config.tuning.retention_description'
						)}
						enter={3}
						example={t('po:config.tuning.retention_example')}
						id='retention'
						title={t('po:config.tuning.retention_title')}
					>
						<RetentionCurve
							onCommit={setRetentionAge}
							values={draft.tuning.retentionDefaults}
						/>
						<SettingList>
							<NumField
								label={t('po:config.tuning.retMature')}
								onCommit={(value) =>
									setTuningNum('retMature', value)
								}
								value={draft.tuning.retMature}
							/>
							<NumField
								label={t('po:config.tuning.retFloor')}
								onCommit={(value) =>
									setTuningNum('retFloor', value)
								}
								value={draft.tuning.retFloor}
							/>
							<NumField
								label={t('po:config.tuning.retCeil')}
								onCommit={(value) =>
									setTuningNum('retCeil', value)
								}
								value={draft.tuning.retCeil}
							/>
						</SettingList>
					</ConfigSection>

					<ConfigSection
						description={t('po:config.tuning.lot_description')}
						enter={3}
						example={t('po:config.tuning.lot_example')}
						id='lot'
						title={t('po:config.tuning.lot_title')}
					>
						<SettingList>
							<NumField
								label={t('po:config.tuning.lotExactBelow')}
								onCommit={(value) =>
									setTuningNum('lotExactBelow', value)
								}
								value={draft.tuning.lotExactBelow}
							/>
							<NumField
								label={t('po:config.tuning.lotMinRecurring')}
								onCommit={(value) =>
									setTuningNum('lotMinRecurring', value)
								}
								value={draft.tuning.lotMinRecurring}
							/>
						</SettingList>
						<LotSteps
							onAdd={addLotStep}
							onPatch={patchLotStep}
							onRemove={removeLotStep}
							steps={draft.tuning.lotSteps}
						/>
					</ConfigSection>
				</div>

				{query.data.history.length > 0 ? (
					<HistoryRail entries={query.data.history} />
				) : null}
			</div>

			<SaveBar
				dirty={dirty}
				onReset={() => {
					setDraft(saved)
					setFormVersion((version) => version + 1)
				}}
				onSave={() => updateConfig.mutateAsync(draft)}
				pending={updateConfig.isPending}
			/>
		</div>
	)
}

type BusinessConfig = PoGetConfigResponse['data']['config']
type Tuning = BusinessConfig['tuning']
type TuningNumKey = {
	[K in keyof Tuning]: Tuning[K] extends number ? K : never
}[keyof Tuning]
type LotStep = Tuning['lotSteps'][number]

// Scalar tuning knobs grouped into sections; each `name` and each key resolves
// to a `po:config.tuning.*` label. Array-valued knobs (retention curve, lot
// steps) and the adoption toggle are rendered on their own.
const TUNING_NUM_GROUPS = [
	{
		name: 'universe',
		keys: [
			'fitMinBase',
			'scorrimentoRecoverMin',
			'scorrimentoMaxOwn',
			'adottatoHistoryThreshold'
		]
	},
	{ name: 'blend', keys: ['blendK', 'blendMinElapsed', 'ytdFloorCap'] },
	{
		name: 'regimes',
		keys: [
			'reorderHorizonDays',
			'leadTimeDays',
			'campaignAnticipoShare',
			'reorderNewTitleShare'
		]
	},
	{ name: 'segment', keys: ['segMinVol', 'segClipLow', 'segClipHigh'] },
	{
		name: 'succession',
		keys: [
			'succDeclineFromMin',
			'succCollapseRatio',
			'succActiveMin',
			'succSimMin',
			'coexistMinYearGap'
		]
	},
	{ name: 'recent', keys: ['edizioneRecenteMaxAge', 'edizioneRecenteMinVol'] }
] as const satisfies readonly { name: string; keys: readonly TuningNumKey[] }[]

const ADOPTION_NUM_KEYS = [
	'adoptionCellPrior',
	'adoptionWeightMin',
	'adoptionWeightMax',
	'adoptionBookingCap',
	'adoptionBookingMin',
	'exclusivityFactorMin',
	'exclusivityFactorMax'
] as const satisfies readonly TuningNumKey[]

// The page's sections in reading order, for the index beside the form.
const SECTIONS = [
	{ id: 'safety', title: 'po:config.safety_title' },
	{ id: 'universe', title: 'po:config.tuning.universe_title' },
	{ id: 'blend', title: 'po:config.tuning.blend_title' },
	{ id: 'regimes', title: 'po:config.tuning.regimes_title' },
	{ id: 'segment', title: 'po:config.tuning.segment_title' },
	{ id: 'succession', title: 'po:config.tuning.succession_title' },
	{ id: 'recent', title: 'po:config.tuning.recent_title' },
	{ id: 'adoption', title: 'po:config.tuning.adoption_title' },
	{ id: 'retention', title: 'po:config.tuning.retention_title' },
	{ id: 'lot', title: 'po:config.tuning.lot_title' }
] as const satisfies readonly { id: string; title: TKey }[]
